import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { usePeriod } from '../lib/period';
import { Badge, Card, CardHeader, Detail, EmptyState, PageHeader, Table, Td, Th, Tr } from '../components/ui';
import { useNumber } from '../components/kit';
import { Loaded, Note, PersonName, PriorityBadge, StatusBadge, TextLink, sourceHref, useCode, useCompany, useDates } from '../components/b-shared';

/**
 * One record behind the figures (CR-008 §5.3), read-only: what it is, who held it, its dates, and its
 * timeline — the moves with who made them and when. The API sends no description, comment, file or internal
 * note, and this page shows none.
 */

type Person = { id: string; fullName: string; fullNameAr: string | null } | null;
type Ref = { id: string; name: string; nameAr: string | null } | null;
type ProjectRef = { id: string; code: string; name: string } | null;

interface TaskRecord {
  type: 'task';
  id: string;
  title: string;
  status: string;
  priority: string | null;
  createdAt: string;
  startedAt: string | null;
  dueAt: string | null;
  completedAt: string | null;
  isLeaderTask: boolean;
  assignedToDepartment: boolean;
  workflowLevel: number | null;
  project: ProjectRef;
  department: Ref;
  assignee: Person;
  createdBy: Person;
  workflowRun: { id: string; name: string; nameAr: string | null; status: string } | null;
  sourceQTask: { id: string; number: string } | null;
  activities: Array<{ action: string; fromStatus: string | null; toStatus: string | null; occurredAt: string; actor: Person }>;
}

interface TicketRecord {
  type: 'ticket';
  id: string;
  number: string;
  subject: string;
  status: string;
  priority: string | null;
  createdAt: string;
  assignedAt: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  viewDeadlineAt: string;
  assignmentDeadlineAt: string;
  project: ProjectRef;
  client: { id: string; code: string; companyName: string; companyNameAr: string | null } | null;
  targetDepartment: Ref;
  assignee: Person;
  views: Array<{ firstViewedAt: string; wasEligible: boolean; user: Person }>;
  activities: Array<{ action: string; occurredAt: string; actor: Person }>;
}

interface QTaskRecord {
  type: 'qtask';
  id: string;
  number: string;
  title: string;
  status: string;
  priority: string | null;
  createdAt: string;
  assignedAt: string | null;
  dueAt: string | null;
  completedAt: string | null;
  department: Ref;
  assignee: Person;
  tasks: Array<{ id: string; status: string }>;
  activities: Array<{ action: string; occurredAt: string; actor: Person }>;
  converted: boolean;
}

type EvidenceRecord = TaskRecord | TicketRecord | QTaskRecord;
type TimelineEntry = { action: string; fromStatus?: string | null; toStatus?: string | null; occurredAt: string; actor: Person };

const TYPES = ['task', 'ticket', 'qtask'] as const;

/** A Q Task's moves — its views, comments and file changes are not part of its timeline here. */
const Q_TASK_MOVES = new Set([
  'q_task.created',
  'q_task.available',
  'q_task.claimed',
  'q_task.assigned',
  'q_task.reassigned',
  'q_task.released',
  'q_task.force_released',
  'q_task.started',
  'q_task.blocked',
  'q_task.unblocked',
  'q_task.rescheduled',
  'q_task.completed',
  'q_task.skipped',
  'q_task.cancelled',
  'q_task.converted',
  'q_task.done_by_tasks',
  'q_task.reopened_by_task',
  'q_task.cancelled_by_tasks',
]);

export function EvidenceItemPage() {
  const { type = '', id = '' } = useParams();
  const { t } = useI18n();
  const { query } = usePeriod();
  const navigate = useNavigate();
  const valid = (TYPES as readonly string[]).includes(type);
  const record = useQuery({
    queryKey: ['executive', 'evidence-item', type, id],
    queryFn: () => api.get<EvidenceRecord>(`/executive/work/evidence/${type}/${id}`),
    enabled: valid && !!id,
  });
  // Back where the reader came from (an employee's evidence, a project, the work page), else to Work.
  const canGoBack = ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0;
  const back = (
    <button
      type="button"
      onClick={() => (canGoBack ? navigate(-1) : navigate(`/work${query ? `?${query}` : ''}`))}
      className="mb-3 inline-flex items-center gap-1.5 text-body-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
    >
      <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
      {t('evidence.back')}
    </button>
  );

  if (!valid) {
    return (
      <>
        {back}
        <Card>
          <EmptyState title={t('common.noResults')} hint={t('evidence.badType')} />
        </Card>
      </>
    );
  }
  return (
    <>
      {back}
      <Loaded query={record}>{(data) => <RecordView record={data} />}</Loaded>
    </>
  );
}

function RecordView({ record }: { record: EvidenceRecord }) {
  const { t } = useI18n();
  const code = useCode();
  const number = record.type === 'task' ? null : record.number;
  const title = record.type === 'ticket' ? record.subject : record.title;
  const timeline: TimelineEntry[] = record.type === 'qtask' ? record.activities.filter((a) => Q_TASK_MOVES.has(a.action)) : record.activities;
  return (
    <>
      <PageHeader
        title={
          <span>
            {number && (
              <span dir="ltr" className="me-2 font-mono text-[var(--text-tertiary)]">
                {number}
              </span>
            )}
            {title}
          </span>
        }
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2">
            <span>{code('evidence.source', record.type === 'task' ? 'TASK' : record.type === 'ticket' ? 'TICKET' : 'Q_TASK')}</span>
            <Badge tone="neutral">
              <Lock className="h-3 w-3" aria-hidden />
              {t('common.readOnly')}
            </Badge>
          </span>
        }
      />
      <div className="space-y-5">
        <div className="grid gap-5 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader title={t('evidence.item.summary')} />
            {record.type === 'task' && <TaskSummary task={record} />}
            {record.type === 'ticket' && <TicketSummary ticket={record} />}
            {record.type === 'qtask' && <QTaskSummary qTask={record} />}
          </Card>
          <Card>
            <CardHeader title={t('evidence.item.dates')} />
            <Dates record={record} />
          </Card>
        </div>
        {record.type === 'ticket' && <TicketViews ticket={record} />}
        {record.type === 'qtask' && <QTaskTasks qTask={record} />}
        <Timeline entries={timeline} />
        <Note>{t('evidence.item.noContent')}</Note>
      </div>
    </>
  );
}

function Rows({ children }: { children: ReactNode }) {
  return <dl className="grid gap-x-8 sm:grid-cols-2">{children}</dl>;
}

function TaskSummary({ task }: { task: TaskRecord }) {
  const { t, localized } = useI18n();
  const { query } = usePeriod();
  const num = useNumber();
  return (
    <Rows>
      <Detail label={t('common.status')}>
        <StatusBadge status={task.status} />
      </Detail>
      <Detail label={t('common.priority')}>
        <PriorityBadge priority={task.priority} />
      </Detail>
      <Detail label={t('evidence.item.assignee')}>
        {task.assignedToDepartment ? t('evidence.item.wholeDepartment') : <PersonName person={task.assignee} />}
      </Detail>
      <Detail label={t('evidence.item.createdBy')}>
        <PersonName person={task.createdBy} />
      </Detail>
      <Detail label={t('common.department')}>{task.department ? localized(task.department) : '—'}</Detail>
      <Detail label={t('common.project')}>
        {task.project ? (
          <TextLink to={`/projects/${task.project.id}${query ? `?${query}` : ''}`}>
            <span dir="ltr" className="me-1 font-mono text-caption">
              {task.project.code}
            </span>
            {task.project.name}
          </TextLink>
        ) : (
          '—'
        )}
      </Detail>
      <Detail label={t('evidence.item.workflow')}>
        {task.workflowRun ? (
          <span>
            {localized(task.workflowRun)}
            {task.workflowLevel !== null && <span className="ms-1 text-caption text-muted">· {t('work.workflows.level', { n: num(task.workflowLevel) })}</span>}
          </span>
        ) : (
          t('evidence.item.noWorkflow')
        )}
      </Detail>
      <Detail label={t('evidence.item.fromQTask')}>
        {task.sourceQTask ? (
          <TextLink to={sourceHref('Q_TASK', task.sourceQTask.id, query) ?? '#'}>
            <span dir="ltr" className="font-mono">
              {task.sourceQTask.number}
            </span>
          </TextLink>
        ) : (
          '—'
        )}
      </Detail>
      {task.isLeaderTask && (
        <Detail label={t('evidence.item.kindLabel')}>
          <Badge tone="plasma">{t('evidence.item.leaderTask')}</Badge>
        </Detail>
      )}
    </Rows>
  );
}

function TicketSummary({ ticket }: { ticket: TicketRecord }) {
  const { t, localized } = useI18n();
  const { query } = usePeriod();
  return (
    <Rows>
      <Detail label={t('common.status')}>
        <StatusBadge status={ticket.status} />
      </Detail>
      <Detail label={t('common.priority')}>
        <PriorityBadge priority={ticket.priority} />
      </Detail>
      <Detail label={t('evidence.item.assignee')}>
        <PersonName person={ticket.assignee} />
      </Detail>
      <Detail label={t('evidence.item.targetDepartment')}>{ticket.targetDepartment ? localized(ticket.targetDepartment) : '—'}</Detail>
      <Detail label={t('common.client')}>
        {ticket.client ? (
          <TextLink to={`/clients/${ticket.client.id}${query ? `?${query}` : ''}`}>{localized(ticket.client)}</TextLink>
        ) : (
          t('common.internal')
        )}
      </Detail>
      <Detail label={t('common.project')}>
        {ticket.project ? (
          <TextLink to={`/projects/${ticket.project.id}${query ? `?${query}` : ''}`}>
            <span dir="ltr" className="me-1 font-mono text-caption">
              {ticket.project.code}
            </span>
            {ticket.project.name}
          </TextLink>
        ) : (
          '—'
        )}
      </Detail>
    </Rows>
  );
}

function QTaskSummary({ qTask }: { qTask: QTaskRecord }) {
  const { t, localized } = useI18n();
  return (
    <Rows>
      <Detail label={t('common.status')}>
        <StatusBadge status={qTask.status} />
      </Detail>
      <Detail label={t('common.priority')}>
        <PriorityBadge priority={qTask.priority} />
      </Detail>
      <Detail label={t('evidence.item.assignee')}>
        <PersonName person={qTask.assignee} />
      </Detail>
      <Detail label={t('common.department')}>{qTask.department ? localized(qTask.department) : '—'}</Detail>
      <Detail label={t('evidence.item.converted')}>{qTask.converted ? t('evidence.item.convertedYes') : t('common.no')}</Detail>
    </Rows>
  );
}

function Dates({ record }: { record: EvidenceRecord }) {
  const { t } = useI18n();
  const dates = useDates();
  const { timezone } = useCompany();
  const at = (v: string | null | undefined) => dates.at(v, timezone);
  return (
    <dl>
      <Detail label={t('evidence.item.created')}>{at(record.createdAt)}</Detail>
      {record.type === 'task' && (
        <>
          <Detail label={t('evidence.item.started')}>{at(record.startedAt)}</Detail>
          <Detail label={t('evidence.col.deadline')}>{at(record.dueAt)}</Detail>
          <Detail label={t('evidence.item.completed')}>{at(record.completedAt)}</Detail>
        </>
      )}
      {record.type === 'ticket' && (
        <>
          <Detail label={t('evidence.item.viewDeadline')}>{at(record.viewDeadlineAt)}</Detail>
          <Detail label={t('evidence.item.assignmentDeadline')}>{at(record.assignmentDeadlineAt)}</Detail>
          <Detail label={t('evidence.item.assigned')}>
            <span className="inline-flex flex-wrap items-center justify-end gap-1.5">
              {at(record.assignedAt)}
              {record.assignedAt && (
                <Badge tone={record.assignedAt <= record.assignmentDeadlineAt ? 'success' : 'error'}>
                  {record.assignedAt <= record.assignmentDeadlineAt ? t('evidence.outcome.onTime') : t('evidence.outcome.late')}
                </Badge>
              )}
            </span>
          </Detail>
          <Detail label={t('evidence.item.resolved')}>{at(record.resolvedAt)}</Detail>
          <Detail label={t('evidence.item.closed')}>{at(record.closedAt)}</Detail>
        </>
      )}
      {record.type === 'qtask' && (
        <>
          <Detail label={t('evidence.item.assigned')}>{at(record.assignedAt)}</Detail>
          <Detail label={t('evidence.col.deadline')}>{at(record.dueAt)}</Detail>
          <Detail label={t('evidence.item.completed')}>{at(record.completedAt)}</Detail>
        </>
      )}
      {timezone && <p className="mt-2 text-caption text-muted">{t('period.timezone', { tz: timezone })}</p>}
    </dl>
  );
}

function TicketViews({ ticket }: { ticket: TicketRecord }) {
  const { t } = useI18n();
  const dates = useDates();
  const { timezone } = useCompany();
  return (
    <Card padded={false}>
      <div className="px-5 pt-5">
        <CardHeader title={t('evidence.item.views')} subtitle={t('evidence.item.viewsHint')} />
      </div>
      {ticket.views.length === 0 ? (
        <p className="px-5 pb-5 text-body-sm text-muted">{t('evidence.item.noViews')}</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{t('common.employee')}</Th>
              <Th>{t('evidence.item.firstViewed')}</Th>
              <Th>{t('evidence.item.eligible')}</Th>
              <Th>{t('evidence.col.outcome')}</Th>
            </tr>
          </thead>
          <tbody>
            {ticket.views.map((v, i) => {
              const inTime = v.firstViewedAt <= ticket.viewDeadlineAt;
              return (
                <Tr key={`${v.user?.id ?? 'x'}-${i}`}>
                  <Td>
                    <PersonName person={v.user} />
                  </Td>
                  <Td className="whitespace-nowrap">{dates.at(v.firstViewedAt, timezone)}</Td>
                  <Td>{v.wasEligible ? t('common.yes') : t('common.no')}</Td>
                  <Td>
                    {v.wasEligible ? (
                      <Badge tone={inTime ? 'success' : 'error'}>{inTime ? t('evidence.outcome.viewedInTime') : t('evidence.outcome.viewedLate')}</Badge>
                    ) : (
                      <span className="text-caption text-muted">{t('evidence.item.notJudged')}</span>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function QTaskTasks({ qTask }: { qTask: QTaskRecord }) {
  const { t } = useI18n();
  const { query } = usePeriod();
  if (!qTask.tasks.length) return null;
  return (
    <Card>
      <CardHeader title={t('evidence.item.becameTasks')} subtitle={t('evidence.item.becameTasksHint')} />
      <ul className="flex flex-wrap gap-2">
        {qTask.tasks.map((task, i) => (
          <li key={task.id} className="inline-flex items-center gap-1.5 rounded-md border hairline px-2.5 py-1.5">
            <TextLink to={sourceHref('TASK', task.id, query) ?? '#'}>{t('evidence.item.taskN', { n: String(i + 1) })}</TextLink>
            <StatusBadge status={task.status} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Timeline({ entries }: { entries: TimelineEntry[] }) {
  const { t, locale } = useI18n();
  const dates = useDates();
  const code = useCode();
  const { timezone } = useCompany();
  return (
    <Card>
      <CardHeader title={t('evidence.item.timeline')} subtitle={t('evidence.item.timelineHint')} />
      {entries.length === 0 ? (
        <p className="text-body-sm text-muted">{t('evidence.item.noTimeline')}</p>
      ) : (
        <ol className="relative space-y-4 border-s hairline ps-5">
          {entries.map((e, i) => (
            <li key={`${e.action}-${e.occurredAt}-${i}`} className="relative">
              <span className="absolute -start-[1.6rem] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-[var(--surface-card)] bg-primary" aria-hidden />
              <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-body-sm text-[var(--text-primary)]">
                <span className="font-medium">{code('evidence.action', e.action.replace(/\./g, '_'))}</span>
                {(e.fromStatus || e.toStatus) && (
                  <span className="inline-flex items-center gap-1">
                    {e.fromStatus && <StatusBadge status={e.fromStatus} />}
                    {e.fromStatus && e.toStatus && (
                      <span aria-hidden className="text-[var(--text-tertiary)]">
                        {locale === 'ar' ? '←' : '→'}
                      </span>
                    )}
                    {e.toStatus && <StatusBadge status={e.toStatus} />}
                  </span>
                )}
              </p>
              <p className="mt-0.5 text-caption text-muted">
                {dates.at(e.occurredAt, timezone)} · {e.actor ? <PersonName person={e.actor} /> : t('evidence.item.system')}
              </p>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
